import React, { useState, useMemo, useRef, useCallback } from 'react';
import { C } from '../tokens';
import Icon from '../components/Icon';
import { Card, Pill, Btn, Breadcrumb } from '../components/UI';
import useBreakpoint from '../useBreakpoint';

/* ═══════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════ */
const FILE_ICONS   = { pdf: 'picture_as_pdf', txt: 'article', json: 'data_object', docx: 'description', csv: 'table_chart', md: 'code' };
const STATUS_DOTS  = { Ready: C.green, Syncing: C.primary, Error: C.red, Queued: C.t3 };
const PAGE_SIZE    = 5;

const INIT_CATS = [
  { label: 'Legal Docs',      icon: 'gavel',       color: '#3b82f6' },
  { label: 'Product Specs',   icon: 'settings',    color: '#a855f7' },
  { label: 'Customer Chats',  icon: 'forum',       color: C.primary },
  { label: 'Marketing',       icon: 'campaign',    color: C.amber   },
];

/* 24 Seed documents */
const SEED = [
  { name: 'Privacy_Policy_v2.pdf',          cat: 'Legal Docs',     chunks: 1240, size: '2.4 MB',  status: 'Ready'   },
  { name: 'Product_Specs_Core_API.txt',     cat: 'Product Specs',  chunks: 856,  size: '450 KB',  status: 'Syncing' },
  { name: 'Support_Logs_Q3.json',           cat: 'Customer Chats', chunks: 5310, size: '12.8 MB', status: 'Ready'   },
  { name: 'Marketing_Strategy_2024.pdf',    cat: 'Marketing',      chunks: 2104, size: '5.1 MB',  status: 'Error'   },
  { name: 'Employee_Handbook_v4.docx',      cat: 'Legal Docs',     chunks: 3890, size: '8.2 MB',  status: 'Ready'   },
  { name: 'API_Reference_Guide.md',         cat: 'Product Specs',  chunks: 1420, size: '1.1 MB',  status: 'Ready'   },
  { name: 'Customer_Feedback_Q2.json',      cat: 'Customer Chats', chunks: 3200, size: '7.4 MB',  status: 'Ready'   },
  { name: 'Brand_Guidelines_2024.pdf',      cat: 'Marketing',      chunks: 980,  size: '3.8 MB',  status: 'Ready'   },
  { name: 'Terms_of_Service_v3.pdf',        cat: 'Legal Docs',     chunks: 1560, size: '2.9 MB',  status: 'Ready'   },
  { name: 'SDK_Integration_Docs.txt',       cat: 'Product Specs',  chunks: 2340, size: '1.8 MB',  status: 'Syncing' },
  { name: 'Live_Chat_Transcripts_Aug.json', cat: 'Customer Chats', chunks: 6780, size: '15.2 MB', status: 'Ready'   },
  { name: 'SEO_Keyword_Research.csv',       cat: 'Marketing',      chunks: 440,  size: '680 KB',  status: 'Ready'   },
  { name: 'NDA_Template_Standard.docx',     cat: 'Legal Docs',     chunks: 320,  size: '280 KB',  status: 'Ready'   },
  { name: 'Webhook_Config_Specs.txt',       cat: 'Product Specs',  chunks: 1100, size: '890 KB',  status: 'Ready'   },
  { name: 'Chatbot_Flow_Diagrams.pdf',      cat: 'Customer Chats', chunks: 1890, size: '4.5 MB',  status: 'Error'   },
  { name: 'Email_Campaign_Results.csv',     cat: 'Marketing',      chunks: 670,  size: '1.2 MB',  status: 'Ready'   },
  { name: 'GDPR_Compliance_Guide.pdf',      cat: 'Legal Docs',     chunks: 2200, size: '4.1 MB',  status: 'Ready'   },
  { name: 'REST_API_Changelog.md',          cat: 'Product Specs',  chunks: 780,  size: '540 KB',  status: 'Syncing' },
  { name: 'Escalation_Procedures.docx',     cat: 'Customer Chats', chunks: 450,  size: '380 KB',  status: 'Ready'   },
  { name: 'Social_Media_Calendar.csv',      cat: 'Marketing',      chunks: 290,  size: '410 KB',  status: 'Queued'  },
  { name: 'Onboarding_Checklist.pdf',       cat: 'Customer Chats', chunks: 340,  size: '520 KB',  status: 'Ready'   },
  { name: 'ML_Model_Training_Set.json',     cat: 'Product Specs',  chunks: 8900, size: '22.4 MB', status: 'Ready'   },
  { name: 'Disaster_Recovery_Plan.docx',    cat: 'Legal Docs',     chunks: 1670, size: '3.2 MB',  status: 'Ready'   },
  { name: 'Intent_Classification.csv',      cat: 'Product Specs',  chunks: 4200, size: '9.7 MB',  status: 'Syncing' },
].map((d, i) => {
  const ext = d.name.split('.').pop();
  return { ...d, id: i + 1, icon: FILE_ICONS[ext] || 'description', ext };
});

/* ═══════════════════════════════════════════════════════════
   Shared UI pieces
   ═══════════════════════════════════════════════════════════ */
function Backdrop({ children, onClose, mob }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(34,22,16,.32)', backdropFilter: 'blur(2px)', display: 'grid', placeItems: 'center', padding: mob ? 12 : 24, animation: 'kbFI .15s' }}>
      <style>{`@keyframes kbFI{from{opacity:0}to{opacity:1}}@keyframes kbSU{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div onClick={e => e.stopPropagation()} style={{ animation: 'kbSU .2s ease', width: '100%', maxWidth: mob ? undefined : 600, display: 'flex', justifyContent: 'center' }}>{children}</div>
    </div>
  );
}

function DeletePopover({ label, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20, marginTop: 4, width: 234, background: C.w, borderRadius: 12, border: `1px solid ${C.bdr}`, boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: 14 }}>
      <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Delete document?</p>
      <p style={{ fontSize: 11, color: C.t2, lineHeight: 1.5, marginBottom: 12, wordBreak: 'break-all' }}><b>{label}</b> will be permanently removed.</p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button onClick={onConfirm} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   KnowledgeBasePage
   ═══════════════════════════════════════════════════════════ */
export default function KnowledgeBasePage() {
  const { isMobile, isTablet, isDesktop, isWide } = useBreakpoint();
  const mob = isMobile;
  const pad = mob ? 16 : isTablet ? 24 : 36;

  /* ─── Core data ─── */
  const [docs, setDocs]           = useState(SEED);
  const [categories, setCats]     = useState(INIT_CATS);
  const nextId                    = useRef(SEED.length + 1);

  /* ─── Table controls ─── */
  const [search, setSearch]       = useState('');
  const [activeCat, setActiveCat] = useState('All Documents');
  const [page, setPage]           = useState(1);
  const [sortCol, setSortCol]     = useState(null);
  const [sortDir, setSortDir]     = useState('asc');
  const [deleteId, setDeleteId]   = useState(null);

  /* ─── Modal flags ─── */
  const [showUpload, setShowUpload] = useState(false);
  const [showNewCat, setShowNewCat] = useState(false);

  /* ─── Upload state ─── */
  const [dragOver, setDragOver]     = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadCat, setUploadCat]   = useState('');
  const fileRef                     = useRef(null);

  /* ─── New-category state ─── */
  const [catName, setCatName]       = useState('');
  const [catIcon, setCatIcon]       = useState('folder');
  const [catColor, setCatColor]     = useState(C.teal);

  /* ═══════════ Derived data ═══════════ */

  /* Category counts (dynamic) */
  const catCounts = useMemo(() => {
    const m = {};
    docs.forEach(d => { m[d.cat] = (m[d.cat] || 0) + 1; });
    return m;
  }, [docs]);

  /* Filtering + sorting */
  const filtered = useMemo(() => {
    let list = [...docs];
    if (activeCat !== 'All Documents') list = list.filter(d => d.cat === activeCat);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.name.toLowerCase().includes(q) ||
        d.cat.toLowerCase().includes(q) ||
        d.status.toLowerCase().includes(q) ||
        d.ext.toLowerCase().includes(q)
      );
    }
    if (sortCol) {
      list.sort((a, b) => {
        let av = a[sortCol], bv = b[sortCol];
        if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return list;
  }, [docs, search, activeCat, sortCol, sortDir]);

  /* Pagination */
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const rows       = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rStart     = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const rEnd       = Math.min(safePage * PAGE_SIZE, filtered.length);

  /* Stats */
  const totalChunks = useMemo(() => docs.reduce((s, d) => s + d.chunks, 0), [docs]);
  const totalSizeMB = useMemo(() => {
    return docs.reduce((s, d) => {
      const n = parseFloat(d.size);
      return s + (d.size.includes('KB') ? n / 1024 : n);
    }, 0);
  }, [docs]);
  const lastSync = useMemo(() => {
    const syncing = docs.filter(d => d.status === 'Syncing').length;
    return syncing > 0 ? `${syncing} syncing` : '2 mins ago';
  }, [docs]);

  /* ═══════════ Actions ═══════════ */
  const doSearch = v => { setSearch(v); setPage(1); };
  const doCat    = c => { setActiveCat(c); setPage(1); setSearch(''); };
  const doSort   = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(1);
  };

  const deleteDoc = id => { setDocs(prev => prev.filter(d => d.id !== id)); setDeleteId(null); };

  const retryDoc = id => {
    setDocs(prev => prev.map(d => d.id === id ? { ...d, status: 'Syncing' } : d));
    setTimeout(() => setDocs(prev => prev.map(d => d.id === id && d.status === 'Syncing' ? { ...d, status: 'Ready' } : d)), 2200);
  };

  /* ═══════════ Upload logic ═══════════ */
  const addFiles = useCallback(fileList => {
    const ok = ['pdf', 'txt', 'docx', 'json', 'csv', 'md'];
    const nf = Array.from(fileList)
      .filter(f => ok.includes(f.name.split('.').pop().toLowerCase()))
      .map(f => ({ file: f, name: f.name, size: f.size, progress: 0, status: 'pending' }));
    if (nf.length) setUploadFiles(prev => [...prev, ...nf]);
  }, []);

  const startUpload = () => {
    if (!uploadCat) return;
    uploadFiles.forEach((uf, idx) => {
      if (uf.status !== 'pending') return;
      setUploadFiles(prev => prev.map((f, i) => i === idx ? { ...f, status: 'uploading' } : f));
      let prog = 0;
      const iv = setInterval(() => {
        prog += Math.random() * 22 + 8;
        if (prog >= 100) {
          prog = 100; clearInterval(iv);
          const ext = uf.name.split('.').pop().toLowerCase();
          const sStr = uf.size > 1048576 ? `${(uf.size / 1048576).toFixed(1)} MB` : `${Math.round(uf.size / 1024)} KB`;
          setDocs(prev => [...prev, {
            id: nextId.current++, name: uf.name, cat: uploadCat,
            chunks: Math.floor(Math.random() * 3000 + 200), size: sStr, status: 'Ready',
            icon: FILE_ICONS[ext] || 'description', ext,
          }]);
          setUploadFiles(prev => prev.map((f, i) => i === idx ? { ...f, progress: 100, status: 'done' } : f));
        } else {
          setUploadFiles(prev => prev.map((f, i) => i === idx ? { ...f, progress: Math.min(100, Math.round(prog)) } : f));
        }
      }, 280);
    });
  };

  const closeUpload = () => { setShowUpload(false); setUploadFiles([]); setUploadCat(''); };
  const onDrop = useCallback(e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }, [addFiles]);

  /* ═══════════ New category ═══════════ */
  const PICK_ICONS   = ['folder', 'gavel', 'settings', 'forum', 'campaign', 'engineering', 'school', 'science', 'code', 'analytics', 'security', 'support_agent'];
  const PICK_COLORS  = ['#3b82f6', '#a855f7', C.primary, C.amber, C.teal, C.indigo, C.green, C.red, '#0ea5e9', '#f472b6'];

  const saveCategory = () => {
    const trimmed = catName.trim();
    if (!trimmed || categories.some(c => c.label.toLowerCase() === trimmed.toLowerCase())) return;
    setCats(prev => [...prev, { label: trimmed, icon: catIcon, color: catColor }]);
    setCatName(''); setCatIcon('folder'); setCatColor(C.teal);
    setShowNewCat(false);
  };
  const closeNewCat = () => { setShowNewCat(false); setCatName(''); setCatIcon('folder'); setCatColor(C.teal); };

  /* ═══════════ Style helpers ═══════════ */
  const inp = { width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.bdr}`, fontSize: 13, outline: 'none', fontFamily: 'inherit', transition: 'border .15s, box-shadow .15s', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: C.t2, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' };
  const foc = {
    onFocus: e => { e.target.style.borderColor = C.primary; e.target.style.boxShadow = `0 0 0 3px ${C.pL}`; },
    onBlur:  e => { e.target.style.borderColor = C.bdr;     e.target.style.boxShadow = 'none'; },
  };

  /* Helper: look up category color */
  const colOf = cat => (categories.find(c => c.label === cat) || {}).color || C.t3;

  /* Sortable header */
  const Th = ({ col, label, sx }) => (
    <th onClick={() => doSort(col)} style={{ padding: mob ? '10px 12px' : '10px 20px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...sx }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {label}
        {sortCol === col
          ? <Icon name={sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'} size={12} style={{ color: C.primary }} />
          : <Icon name="unfold_more" size={12} style={{ color: '#d6d3d1' }} />}
      </span>
    </th>
  );

  /* Show two-column layout only on wide desktop */
  const twoCol = isWide;

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
  <>
    <div style={{ height: '100%', overflow: 'auto' }}>
    <div style={{ padding: `${mob ? 16 : 32}px ${pad}px 48px` }}>
      {!mob && <Breadcrumb items={['Home', 'Knowledge Base']} />}

      {/* ─── Header + Stats ─── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: mob ? 'flex-start' : 'flex-end', marginBottom: mob ? 20 : 28, flexWrap: 'wrap', gap: mob ? 12 : 16 }}>
        <div>
          <h2 style={{ fontSize: mob ? 24 : 32, fontWeight: 900, letterSpacing: '-.03em' }}>Knowledge Base</h2>
          {!mob && <p style={{ fontSize: 13, color: C.t2, marginTop: 4 }}>Manage and process your AI training data</p>}
        </div>
        <div style={{ display: 'flex', gap: mob ? 8 : 14, flexWrap: 'wrap', width: mob ? '100%' : 'auto' }}>
          {[
            ['Total Chunks', totalChunks.toLocaleString(), C.primary],
            ['Storage Used', `${totalSizeMB.toFixed(1)} MB`, C.t1],
            ['Last Sync', lastSync, C.t1],
          ].map(([l, v, c]) => (
            <Card key={l} style={{ padding: mob ? '10px 14px' : '14px 20px', minWidth: mob ? 0 : 130, flex: mob ? 1 : 'none' }}>
              <div style={{ fontSize: mob ? 9 : 10, fontWeight: 600, color: C.t3, textTransform: 'uppercase', marginBottom: mob ? 2 : 4 }}>{l}</div>
              <div style={{ fontSize: mob ? 16 : 22, fontWeight: 800, color: c }}>{v}</div>
            </Card>
          ))}
        </div>
      </div>

      {/* ─── Mobile / tablet: horizontal category pills ─── */}
      {!twoCol && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4 }}>
          {[{ label: 'All Documents', icon: 'folder', color: C.primary }, ...categories].map(cat => {
            const active = activeCat === cat.label;
            const cnt = cat.label === 'All Documents' ? docs.length : (catCounts[cat.label] || 0);
            return (
              <button key={cat.label} onClick={() => doCat(cat.label)} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 99, border: active ? 'none' : `1px solid ${C.bdr}`,
                background: active ? C.primary : C.w, color: active ? '#fff' : C.t2, fontSize: 12, fontWeight: active ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                <Icon name={cat.icon} size={14} style={{ color: active ? '#fff' : C.t3 }} />
                {cat.label}
                <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 2, opacity: 0.8 }}>{cnt}</span>
              </button>
            );
          })}
          <button onClick={() => setShowNewCat(true)} style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 99, border: `1px dashed ${C.bdr}`,
            background: C.w, color: C.t3, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            <Icon name="add" size={14} /> Add
          </button>
        </div>
      )}

      {/* ─── Two-column grid ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: twoCol ? '1fr 280px' : '1fr', gap: mob ? 16 : 24 }}>
        {/* ══ LEFT — Main content ══ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: mob ? 14 : 24 }}>

          {/* Upload drop zone */}
          <div onClick={() => setShowUpload(true)} style={{
            border: `2px dashed ${C.primary}50`, borderRadius: 14,
            background: C.pL, padding: mob ? '28px 16px' : '48px 20px', display: 'flex', flexDirection: 'column',
            alignItems: 'center', textAlign: 'center', cursor: 'pointer', transition: 'background .2s',
          }}
            onMouseEnter={e => { if (isDesktop) e.currentTarget.style.background = `${C.primary}14`; }}
            onMouseLeave={e => { if (isDesktop) e.currentTarget.style.background = C.pL; }}
          >
            <div style={{ width: mob ? 44 : 56, height: mob ? 44 : 56, borderRadius: '50%', background: `${C.primary}20`, display: 'grid', placeItems: 'center', marginBottom: mob ? 10 : 16 }}>
              <Icon name="cloud_upload" size={mob ? 24 : 30} style={{ color: C.primary }} />
            </div>
            <h3 style={{ fontSize: mob ? 15 : 18, fontWeight: 700, marginBottom: mob ? 4 : 8 }}>Upload New Documents</h3>
            {!mob && (
              <p style={{ fontSize: 13, color: C.t2, maxWidth: 360, lineHeight: 1.6, marginBottom: 18 }}>
                Drag and drop your PDF, TXT, or DOCX files here. We'll automatically chunk and vectorize them for your AI.
              </p>
            )}
            <Btn primary>{mob ? 'Browse' : 'Browse Files'}</Btn>
          </div>

          {/* Search bar */}
          <div style={{ position: 'relative' }}>
            <Icon name="search" size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.t3, pointerEvents: 'none' }} />
            <input value={search} onChange={e => doSearch(e.target.value)}
              placeholder={mob ? 'Search documents...' : 'Search documents by name, category, or status...'}
              style={{ width: '100%', padding: '11px 16px 11px 42px', borderRadius: 12, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, outline: 'none', transition: 'border .15s', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = C.primary}
              onBlur={e => e.target.style.borderColor = C.bdr} />
            {search && (
              <button onClick={() => doSearch('')} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: C.t3, display: 'grid', placeItems: 'center' }}>
                <Icon name="close" size={16} />
              </button>
            )}
          </div>

          {/* ─── Documents table / cards ─── */}
          <Card style={{ overflow: 'hidden' }}>
            {/* Table header bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: mob ? '14px 16px' : '18px 24px', borderBottom: `1px solid ${C.bdr}` }}>
              <div>
                <h3 style={{ fontSize: mob ? 14 : 17, fontWeight: 700 }}>{mob ? 'Documents' : 'Knowledge Base Documents'}</h3>
                {(search || activeCat !== 'All Documents') && (
                  <p style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>
                    {search ? `Found ${filtered.length} result${filtered.length !== 1 ? 's' : ''} for "${search}"` : `${filtered.length} in ${activeCat}`}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {sortCol && (
                  <button onClick={() => { setSortCol(null); setSortDir('asc'); }} title="Clear sort"
                    style={{ border: 'none', background: C.pL, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, fontSize: 10, fontWeight: 600, color: C.primary, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="filter_list_off" size={14} /> Clear
                  </button>
                )}
                <button title="Sync all"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: C.t2, display: 'grid', placeItems: 'center' }}>
                  <Icon name="sync" size={20} />
                </button>
              </div>
            </div>

            {/* ─── Mobile: Card-based list ─── */}
            {mob ? (
              <div style={{ padding: '8px 0' }}>
                {rows.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <Icon name="search_off" size={32} style={{ color: C.t3, display: 'block', margin: '0 auto 10px' }} />
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, marginBottom: 4 }}>No documents found</div>
                    <div style={{ fontSize: 12, color: C.t3 }}>Try adjusting your search or filter</div>
                  </div>
                ) : rows.map(d => {
                  const cc = colOf(d.cat);
                  return (
                    <div key={d.id} style={{ padding: '12px 16px', borderBottom: `1px solid ${C.bdr}`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <Icon name={d.icon} size={20} style={{ color: cc, flexShrink: 0, marginTop: 2 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                          <Pill bg={`${cc}14`} color={cc}>{d.cat}</Pill>
                          <span style={{ fontSize: 11, color: C.t2 }}>{d.chunks.toLocaleString()} chunks</span>
                          <span style={{ fontSize: 11, color: C.t3 }}>{d.size}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%', display: 'inline-block',
                            background: STATUS_DOTS[d.status] || C.t3,
                            animation: d.status === 'Syncing' ? 'kbPulse 1.4s infinite' : 'none',
                          }} />
                          <span style={{ fontSize: 11, fontWeight: 500, color: STATUS_DOTS[d.status] || C.t2 }}>{d.status}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {d.status === 'Error' && (
                          <button onClick={() => retryDoc(d.id)} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: C.pL, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.primary }}>
                            <Icon name="refresh" size={16} />
                          </button>
                        )}
                        <button onClick={() => setDeleteId(deleteId === d.id ? null : d.id)} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t3, position: 'relative' }}>
                          <Icon name="delete" size={16} />
                          {deleteId === d.id && <DeletePopover label={d.name} onConfirm={() => deleteDoc(d.id)} onCancel={() => setDeleteId(null)} />}
                        </button>
                      </div>
                    </div>
                  );
                })}
                <style>{`@keyframes kbPulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
              </div>
            ) : (
              /* ─── Desktop / Tablet: Table ─── */
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: isTablet ? 600 : 700 }}>
                  <thead>
                    <tr style={{ background: C.bg }}>
                      <Th col="name"   label="Name" />
                      <Th col="cat"    label="Category" />
                      {!isTablet && <Th col="chunks" label="Chunks" sx={{ textAlign: 'center' }} />}
                      {!isTablet && <Th col="size"   label="Size" />}
                      <Th col="status" label="Status" />
                      <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={isTablet ? 4 : 6} style={{ padding: 56, textAlign: 'center' }}>
                          <Icon name="search_off" size={36} style={{ color: C.t3, display: 'block', margin: '0 auto 10px' }} />
                          <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, marginBottom: 4 }}>No documents found</div>
                          <div style={{ fontSize: 12, color: C.t3 }}>Try adjusting your search or category filter</div>
                        </td>
                      </tr>
                    ) : rows.map(d => {
                      const cc = colOf(d.cat);
                      return (
                        <tr key={d.id} className="hover-row" style={{ borderBottom: `1px solid ${C.bdr}` }}>
                          <td style={{ padding: '14px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <Icon name={d.icon} size={20} style={{ color: cc, flexShrink: 0 }} />
                              <span style={{ fontWeight: 600 }}>{d.name}</span>
                            </div>
                          </td>
                          <td style={{ padding: '14px 20px' }}>
                            <Pill bg={`${cc}14`} color={cc}>{d.cat}</Pill>
                          </td>
                          {!isTablet && <td style={{ padding: '14px 20px', textAlign: 'center', fontWeight: 500 }}>{d.chunks.toLocaleString()}</td>}
                          {!isTablet && <td style={{ padding: '14px 20px', color: C.t2 }}>{d.size}</td>}
                          <td style={{ padding: '14px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{
                                width: 7, height: 7, borderRadius: '50%', display: 'inline-block',
                                background: STATUS_DOTS[d.status] || C.t3,
                                boxShadow: d.status === 'Syncing' ? `0 0 6px ${C.primary}66` : 'none',
                                animation: d.status === 'Syncing' ? 'kbPulse 1.4s infinite' : 'none',
                              }} />
                              <style>{`@keyframes kbPulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
                              <span style={{ fontWeight: 500, color: STATUS_DOTS[d.status] || C.t2 }}>{d.status}</span>
                            </div>
                          </td>
                          <td style={{ padding: '14px 20px', textAlign: 'right', position: 'relative' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                              {d.status === 'Error' && (
                                <button onClick={() => retryDoc(d.id)} title="Retry"
                                  style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t3, transition: 'color .15s' }}
                                  onMouseEnter={e => e.currentTarget.style.color = C.primary}
                                  onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                                  <Icon name="refresh" size={15} />
                                </button>
                              )}
                              <button onClick={() => setDeleteId(deleteId === d.id ? null : d.id)} title="Delete"
                                style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t3, transition: 'color .15s' }}
                                onMouseEnter={e => e.currentTarget.style.color = C.red}
                                onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                                <Icon name="delete" size={15} />
                              </button>
                            </div>
                            {deleteId === d.id && <DeletePopover label={d.name} onConfirm={() => deleteDoc(d.id)} onCancel={() => setDeleteId(null)} />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ─── Pagination footer ─── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: mob ? '12px 16px' : '14px 20px', borderTop: `1px solid ${C.bdr}`, fontSize: 12, color: C.t2, flexWrap: 'wrap', gap: 8 }}>
              <span>{mob ? `${rStart}–${rEnd} of ${filtered.length}` : <>Showing <b>{rStart}</b>–<b>{rEnd}</b> of <b>{filtered.length}</b> documents</>}</span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
                  style={{ padding: mob ? '6px 10px' : '6px 14px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 12, fontWeight: 600, cursor: safePage <= 1 ? 'default' : 'pointer', color: safePage <= 1 ? '#ddd' : C.t2 }}>
                  {mob ? <Icon name="chevron_left" size={16} /> : 'Previous'}
                </button>
                {!mob && totalPages > 1 && Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button key={n} onClick={() => setPage(n)}
                    style={{ width: 28, height: 28, borderRadius: 8, fontSize: 11, fontWeight: 600, display: 'grid', placeItems: 'center', border: n === safePage ? 'none' : `1px solid ${C.bdr}`, background: n === safePage ? C.primary : C.w, color: n === safePage ? '#fff' : C.t2, cursor: 'pointer' }}>
                    {n}
                  </button>
                ))}
                {mob && <span style={{ fontSize: 12, fontWeight: 600, padding: '0 6px' }}>{safePage}/{totalPages}</span>}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                  style={{ padding: mob ? '6px 10px' : '6px 14px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 12, fontWeight: 600, cursor: safePage >= totalPages ? 'default' : 'pointer', color: safePage >= totalPages ? '#ddd' : C.t2 }}>
                  {mob ? <Icon name="chevron_right" size={16} /> : 'Next'}
                </button>
              </div>
            </div>
          </Card>
        </div>

        {/* ══ RIGHT — Sidebar (wide desktop only) ══ */}
        {twoCol && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Categories card */}
            <Card style={{ padding: 20 }}>
              <h4 style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 16 }}>Categories</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* All Documents */}
                <button onClick={() => doCat('All Documents')} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, width: '100%',
                  background: activeCat === 'All Documents' ? C.pL : 'transparent',
                  color: activeCat === 'All Documents' ? C.primary : C.t2,
                  fontWeight: activeCat === 'All Documents' ? 700 : 500,
                  transition: 'all .12s',
                }}>
                  <Icon name="folder" size={18} />
                  <span style={{ flex: 1, textAlign: 'left' }}>All Documents</span>
                  <span style={{ fontSize: 11, fontWeight: 700, background: activeCat === 'All Documents' ? C.primary : '#f1eeec', color: activeCat === 'All Documents' ? '#fff' : C.t3, padding: '2px 8px', borderRadius: 99, transition: 'all .12s' }}>
                    {docs.length}
                  </span>
                </button>

                {/* Per-category */}
                {categories.map(cat => {
                  const active = activeCat === cat.label;
                  const cnt = catCounts[cat.label] || 0;
                  return (
                    <button key={cat.label} onClick={() => doCat(cat.label)} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, width: '100%',
                      background: active ? C.pL : 'transparent',
                      color: active ? C.primary : C.t2,
                      fontWeight: active ? 700 : 500,
                      transition: 'all .12s',
                    }}>
                      <Icon name={cat.icon} size={18} />
                      <span style={{ flex: 1, textAlign: 'left' }}>{cat.label}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, background: active ? C.primary : '#f1eeec', color: active ? '#fff' : C.t3, padding: '2px 8px', borderRadius: 99, transition: 'all .12s' }}>
                        {cnt}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* + New Category button */}
              <button onClick={() => setShowNewCat(true)} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '10px 0', borderRadius: 10,
                border: `1px solid ${C.bdr}`, background: C.w, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: C.t2, marginTop: 12, transition: 'all .15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.bdr; e.currentTarget.style.color = C.t2; }}>
                <Icon name="add" size={16} /> New Category
              </button>
            </Card>

            {/* Upgrade CTA */}
            <Card style={{ padding: 20, background: C.primary, border: 'none' }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Need more space?</h4>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,.8)', lineHeight: 1.5, marginBottom: 14 }}>
                Upgrade to Pro to increase your knowledge base storage to 50 GB.
              </p>
              <button style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: 'none', background: C.w, color: C.primary, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Upgrade Now
              </button>
            </Card>
          </div>
        )}
      </div>
    </div>
    </div>

    {/* ═══════════════════════════════════════════════════════════
        UPLOAD DOCUMENTS MODAL
       ═══════════════════════════════════════════════════════════ */}
    {showUpload && (
      <Backdrop onClose={closeUpload} mob={mob}>
        <div style={{ width: mob ? '100%' : 580, maxHeight: mob ? '95vh' : '92vh', background: C.w, borderRadius: mob ? 16 : 18, boxShadow: '0 24px 64px rgba(0,0,0,.14)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Modal header */}
          <div style={{ padding: mob ? '16px 20px' : '22px 28px', borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.pL, display: 'grid', placeItems: 'center' }}>
                <Icon name="cloud_upload" size={20} style={{ color: C.primary }} />
              </div>
              <div>
                <h3 style={{ fontSize: mob ? 15 : 17, fontWeight: 700, lineHeight: 1.15 }}>Upload Documents</h3>
                {!mob && <p style={{ fontSize: 11, color: C.t3, marginTop: 1 }}>Add files to your knowledge base for AI processing</p>}
              </div>
            </div>
            <button onClick={closeUpload} style={{ border: 'none', background: '#f1eeec', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t2 }}>
              <Icon name="close" size={18} />
            </button>
          </div>

          {/* Modal body */}
          <div style={{ padding: mob ? '16px 20px' : '24px 28px', overflowY: 'auto', flex: 1 }}>
            {/* Category picker */}
            <div style={{ marginBottom: 18 }}>
              <label style={lbl}>Assign to Category <span style={{ color: C.red }}>*</span></label>
              <select value={uploadCat} onChange={e => setUploadCat(e.target.value)} style={{ ...inp, cursor: 'pointer', appearance: 'auto' }}>
                <option value="">Select category...</option>
                {categories.map(c => <option key={c.label} value={c.label}>{c.label}</option>)}
              </select>
            </div>

            {/* Drag-and-drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? C.primary : C.bdr}`,
                borderRadius: 16, padding: mob ? '24px 16px' : '36px 20px', textAlign: 'center', cursor: 'pointer',
                background: dragOver ? C.pL : '#faf9f8', transition: 'all .2s', marginBottom: 16,
              }}
            >
              <input ref={fileRef} type="file" multiple accept=".pdf,.txt,.docx,.json,.csv,.md" style={{ display: 'none' }}
                onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
              <div style={{ width: mob ? 40 : 52, height: mob ? 40 : 52, borderRadius: 16, background: C.pL, display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
                <Icon name="upload_file" size={mob ? 22 : 26} style={{ color: C.primary }} />
              </div>
              <div style={{ fontSize: mob ? 13 : 14, fontWeight: 700, marginBottom: 4 }}>Drop files here or click to browse</div>
              <div style={{ fontSize: 12, color: C.t3 }}>Supports <b>PDF, TXT, DOCX, JSON, CSV, MD</b></div>
            </div>

            {/* File list */}
            {uploadFiles.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>
                  {uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''} selected
                </div>
                {uploadFiles.map((uf, i) => {
                  const ext = uf.name.split('.').pop().toLowerCase();
                  const ic  = FILE_ICONS[ext] || 'description';
                  const sz  = uf.size > 1048576 ? `${(uf.size / 1048576).toFixed(1)} MB` : `${Math.round(uf.size / 1024)} KB`;
                  const done = uf.status === 'done';
                  const uping = uf.status === 'uploading';
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, border: `1px solid ${done ? C.green + '44' : C.bdr}`, background: done ? `${C.green}06` : C.w, transition: 'all .2s' }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: done ? `${C.green}12` : C.pL, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                        <Icon name={done ? 'check' : ic} size={18} style={{ color: done ? C.green : C.primary }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{uf.name}</div>
                        <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>{sz}{done ? ' \u2022 Processed' : ''}</div>
                        {uping && (
                          <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: '#f1eeec', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${uf.progress}%`, background: C.primary, borderRadius: 2, transition: 'width .3s ease' }} />
                          </div>
                        )}
                      </div>
                      {done && <Icon name="check_circle" size={18} style={{ color: C.green, flexShrink: 0 }} />}
                      {uping && <span style={{ fontSize: 11, fontWeight: 700, color: C.primary, flexShrink: 0 }}>{uf.progress}%</span>}
                      {uf.status === 'pending' && (
                        <button onClick={e => { e.stopPropagation(); setUploadFiles(prev => prev.filter((_, j) => j !== i)); }}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.t3, display: 'grid', placeItems: 'center', flexShrink: 0 }}
                          onMouseEnter={e => e.currentTarget.style.color = C.red}
                          onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                          <Icon name="close" size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Hint */}
            <div style={{ marginTop: 18, padding: '12px 16px', borderRadius: 12, background: '#f1eeec', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <Icon name="info" size={16} style={{ color: C.t3, flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.6 }}>
                Uploaded documents are automatically chunked and vectorized for AI retrieval.
              </div>
            </div>
          </div>

          {/* Modal footer */}
          <div style={{ padding: mob ? '14px 20px' : '16px 28px', borderTop: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, gap: 8 }}>
            <span style={{ fontSize: 11, color: C.t3 }}>
              {uploadFiles.filter(f => f.status === 'done').length > 0
                ? `${uploadFiles.filter(f => f.status === 'done').length} of ${uploadFiles.length} uploaded`
                : uploadFiles.length > 0 ? `${uploadFiles.length} file${uploadFiles.length !== 1 ? 's' : ''} ready` : ''}
            </span>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn onClick={closeUpload}>{uploadFiles.some(f => f.status === 'done') ? 'Done' : 'Cancel'}</Btn>
              {uploadFiles.some(f => f.status === 'pending') && (
                <Btn primary icon="cloud_upload" onClick={startUpload}
                  style={{ opacity: uploadCat ? 1 : 0.5, pointerEvents: uploadCat ? 'auto' : 'none' }}>
                  {mob ? 'Upload' : `Upload ${uploadFiles.filter(f => f.status === 'pending').length} File${uploadFiles.filter(f => f.status === 'pending').length !== 1 ? 's' : ''}`}
                </Btn>
              )}
            </div>
          </div>
        </div>
      </Backdrop>
    )}

    {/* ═══════════════════════════════════════════════════════════
        NEW CATEGORY MODAL
       ═══════════════════════════════════════════════════════════ */}
    {showNewCat && (
      <Backdrop onClose={closeNewCat} mob={mob}>
        <div style={{ width: mob ? '100%' : 440, background: C.w, borderRadius: mob ? 16 : 18, boxShadow: '0 24px 64px rgba(0,0,0,.14)', overflow: 'hidden', maxHeight: mob ? '95vh' : undefined, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ padding: mob ? '16px 20px' : '22px 28px', borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.pL, display: 'grid', placeItems: 'center' }}>
                <Icon name="create_new_folder" size={20} style={{ color: C.primary }} />
              </div>
              <div>
                <h3 style={{ fontSize: mob ? 15 : 17, fontWeight: 700, lineHeight: 1.15 }}>New Category</h3>
                {!mob && <p style={{ fontSize: 11, color: C.t3, marginTop: 1 }}>Organize your knowledge base documents</p>}
              </div>
            </div>
            <button onClick={closeNewCat} style={{ border: 'none', background: '#f1eeec', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t2 }}>
              <Icon name="close" size={18} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: mob ? '16px 20px' : '24px 28px', display: 'flex', flexDirection: 'column', gap: 18, overflowY: 'auto', flex: 1 }}>
            {/* Name */}
            <div>
              <label style={lbl}>Category Name <span style={{ color: C.red }}>*</span></label>
              <input value={catName} onChange={e => setCatName(e.target.value)} placeholder="e.g. Training Data" autoFocus style={inp} {...foc}
                onKeyDown={e => e.key === 'Enter' && saveCategory()} />
              {catName.trim() && categories.some(c => c.label.toLowerCase() === catName.trim().toLowerCase()) && (
                <p style={{ fontSize: 11, color: C.red, marginTop: 4 }}>A category with this name already exists.</p>
              )}
            </div>

            {/* Icon picker */}
            <div>
              <label style={lbl}>Icon</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {PICK_ICONS.map(ic => (
                  <button key={ic} onClick={() => setCatIcon(ic)} style={{
                    width: 40, height: 40, borderRadius: 10, display: 'grid', placeItems: 'center', cursor: 'pointer', transition: 'all .15s',
                    border: catIcon === ic ? `2px solid ${catColor}` : `1px solid ${C.bdr}`,
                    background: catIcon === ic ? `${catColor}12` : C.w,
                    color: catIcon === ic ? catColor : C.t3,
                  }}>
                    <Icon name={ic} size={20} />
                  </button>
                ))}
              </div>
            </div>

            {/* Color picker */}
            <div>
              <label style={lbl}>Color</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {PICK_COLORS.map(col => (
                  <button key={col} onClick={() => setCatColor(col)} style={{
                    width: 28, height: 28, borderRadius: '50%', border: catColor === col ? `3px solid ${col}` : '2px solid transparent',
                    background: col, cursor: 'pointer', transition: 'all .15s',
                    outline: catColor === col ? `2px solid ${C.w}` : 'none', outlineOffset: -4,
                  }} />
                ))}
              </div>
            </div>

            {/* Live preview */}
            {catName.trim() && (
              <div style={{ padding: '12px 16px', borderRadius: 10, background: '#faf9f8', border: `1px solid ${C.bdr}` }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: C.t3, textTransform: 'uppercase', marginBottom: 8 }}>Preview</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: `${catColor}14`, display: 'grid', placeItems: 'center' }}>
                    <Icon name={catIcon} size={16} style={{ color: catColor }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{catName.trim()}</span>
                  <Pill bg={`${catColor}14`} color={catColor}>SAMPLE TAG</Pill>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: mob ? '14px 20px' : '16px 28px', borderTop: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: C.t3 }}><span style={{ color: C.red }}>*</span> Required</span>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn onClick={closeNewCat}>Cancel</Btn>
              <Btn primary icon="add" onClick={saveCategory}
                style={{ opacity: catName.trim() && !categories.some(c => c.label.toLowerCase() === catName.trim().toLowerCase()) ? 1 : 0.5, pointerEvents: catName.trim() && !categories.some(c => c.label.toLowerCase() === catName.trim().toLowerCase()) ? 'auto' : 'none' }}>
                {mob ? 'Create' : 'Create Category'}
              </Btn>
            </div>
          </div>
        </div>
      </Backdrop>
    )}
  </>
  );
}
